/**
 * The order the template editor previews with (z8nrz7f7dz): the three lines of
 * the Cryoport sample PO — 10, 100 and 200 at 1.00, 21% VAT — so a tenant sees
 * their template on a realistic page before any real order exists.
 */

import { computePoTotals } from '@/lib/poTotals'
import type { PoTemplateLayout, PurchaseOrderDetail, PurchaseOrderLine } from '@/types/purchaseOrders'
import { formatPoNumber } from './format'

const SAMPLE_LINES: Omit<PurchaseOrderLine, 'lineTotal' | 'taxPct'>[] = [
  { id: 's1', lineNo: 1, materialId: 'sample-1', articleNo: 'CX-1001', supplierRef: 'SR-501', description: 'Cryogenic vial 2 ml', qty: 10, uom: 'ea', unitPrice: 1, discountPct: 0 },
  { id: 's2', lineNo: 2, materialId: 'sample-2', articleNo: 'CX-2040', supplierRef: 'SR-502', description: 'Cryo storage box', qty: 100, uom: 'ea', unitPrice: 1, discountPct: 0 },
  { id: 's3', lineNo: 3, materialId: 'sample-3', articleNo: 'CX-3300', supplierRef: 'SR-503', description: 'Shipper liner', qty: 200, uom: 'ea', unitPrice: 1, discountPct: 0 },
]

export function samplePurchaseOrder(layout: PoTemplateLayout, now = new Date()): PurchaseOrderDetail {
  const taxRatePct = layout.document.defaults.taxRatePct
  const totals = computePoTotals(SAMPLE_LINES, { taxRatePct, shippingCost: 0 })
  const iso = now.toISOString()
  return {
    id: 'sample',
    number: formatPoNumber(layout.document.numbering, 1, now),
    status: 'DRAFT',
    revision: 0,
    supplierName: 'Sample Supplier Ltd',
    currency: layout.document.currency,
    lineCount: SAMPLE_LINES.length,
    grandTotal: totals.grandTotal,
    requestedDeliveryDate: iso.slice(0, 10),
    createdAt: iso,
    sentAt: null,
    supplierContact: 'Alex Morgan',
    supplierAddress: '12 Harbour Road, 2000 Antwerp, Belgium',
    supplierPhone: '+32 3 000 00 00',
    supplierEmail: 'orders@supplier.example',
    supplierTaxId: 'BE0123456789',
    shipTo: null,
    paymentTerms: layout.document.defaults.paymentTerms,
    incoterms: layout.document.defaults.incoterms,
    shippingMethod: 'Courier',
    buyer: 'Purchasing',
    reference: 'REF-001',
    notes: layout.textBlocks.defaultNotes || null,
    taxRatePct,
    shippingCost: 0,
    createdBy: null,
    receivedAt: null,
    cancelledAt: null,
    cancelReason: null,
    lines: SAMPLE_LINES.map((l, i) => ({ ...l, taxPct: null, lineTotal: totals.lineTotals[i] })),
    totals: {
      subtotal: totals.subtotal,
      discountTotal: totals.discountTotal,
      tax: totals.tax,
      shipping: totals.shipping,
      grandTotal: totals.grandTotal,
    },
    revisions: [],
    templateVersion: null,
    templateSnapshot: null,
    customFields: Object.fromEntries(layout.customFields.map((f) => [f.key, f.type === 'number' ? 0 : `Sample ${f.label}`])),
  }
}
